import { useState } from 'react';
import { Check, Plus, MapPin, Edit } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { CommandItem } from '@/components/ui/command';
import { ServerSearchCombobox } from '@/components/ui/server-search-combobox';
import { useLingui } from '@lingui/react/macro';
import { useAddress, useAddressesSearch } from '@/hooks/useAddresses';
import type { Address } from '@/types/address';
import { useQueryClient } from '@tanstack/react-query';
import { AddressForm } from './AddressForm';
import { AddressEditDialog } from '@/components/AddressEditDialog';

interface AddressSelectorProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
}

// Postal lines: "street number", the info line, then "zip city, country".
export function formatAddressLines(address: Address): string[] {
  const streetLine = [address.street, address.houseNumber].filter(Boolean).join(' ');
  const cityLine = [address.zip, address.city].filter(Boolean).join(' ');
  const localityLine = [cityLine, address.countryIso].filter(Boolean).join(', ');
  return [streetLine, address.info2, localityLine].filter((line): line is string => !!line);
}

function formatAddress(address: Address): string {
  return formatAddressLines(address).join(', ');
}

export function AddressSelector({
  value,
  onChange,
  placeholder,
  disabled = false,
}: AddressSelectorProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [search, setSearch] = useState('');

  // Server-side search across all address fields — no client-side cutoff.
  const { data: addresses = [], isLoading, isFetching } = useAddressesSearch(search);

  // Resolve the selected address by id so the trigger label survives even when
  // the selected row is outside the current search result page.
  const { data: selectedAddress } = useAddress(value);

  const invalidateAddresses = () => {
    queryClient.invalidateQueries({ queryKey: ['addresses'] });
    queryClient.invalidateQueries({ queryKey: ['address', value] });
  };

  const handleAddressCreated = (newAddress: Address) => {
    if (!newAddress.id) return;
    onChange(newAddress.id);
    setShowCreateForm(false);
    setSearch('');
    invalidateAddresses();
  };

  const handleAddressUpdated = (newAddressId?: string) => {
    if (newAddressId) {
      onChange(newAddressId);
    }
    invalidateAddresses();
  };

  const resolvedSelected = value ? selectedAddress ?? undefined : undefined;

  return (
    <>
      <div className="flex items-center gap-2">
        <ServerSearchCombobox<Address>
          value={value}
          onChange={(id) => onChange(id)}
          items={addresses}
          selectedItem={resolvedSelected}
          getItemId={(address) => address.id ?? ''}
          onDebouncedSearchChange={setSearch}
          isLoading={isLoading}
          isFetching={isFetching}
          disabled={disabled}
          searchPlaceholder={t`Search...`}
          emptyMessage={t`No addresses found`}
          loadingMessage={t`Loading addresses...`}
          // Grows to fit the address lines instead of truncating to one row.
          triggerClassName="flex-1 min-w-0 max-w-full h-auto min-h-10 whitespace-normal"
          triggerAriaLabel={resolvedSelected ? formatAddress(resolvedSelected) : t`Select address...`}
          renderTrigger={(selected) =>
            selected ? (
              <span className="flex flex-1 items-center gap-2 min-w-0">
                <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="flex min-w-0 flex-col text-left break-words">
                  {formatAddressLines(selected).map((line) => (
                    <span key={line}>{line}</span>
                  ))}
                </span>
              </span>
            ) : (
              <span className="flex-1 truncate text-left text-muted-foreground min-w-0">
                {placeholder || t`Select address...`}
              </span>
            )
          }
          renderItem={(address, isSelected) => (
            <>
              <Check
                className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
                aria-hidden="true"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <span className="text-sm">{formatAddress(address)}</span>
                </div>
              </div>
            </>
          )}
          renderFooter={() => (
            <CommandItem value="__create_address__" onSelect={() => setShowCreateForm(true)}>
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              {t`Create new address`}
            </CommandItem>
          )}
        />

        {value && selectedAddress && !disabled && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setShowEditDialog(true)}
            aria-label={t`Edit address`}
            title={t`Edit address`}
          >
            <Edit className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </div>

      {showCreateForm && (
        <AddressForm
          onClose={() => setShowCreateForm(false)}
          onSuccess={handleAddressCreated}
        />
      )}

      {showEditDialog && value && (
        <AddressEditDialog
          addressId={value}
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          onAddressUpdated={handleAddressUpdated}
        />
      )}
    </>
  );
}
